import React, { useMemo } from 'react';

import { Button } from 'lighthouse';

import AddToCartButton from '@/widgets/AddToCartButton';
import { SafeImage } from '@/widgets/SafeImage';
import { RatingStarIcon } from '@/widgets/StarRating';
import { StoreCityBadge } from '@/widgets/StoreCityBadge';

import { calculateSatisfaction } from '@/utilities/calculateSatisfaction';
import { rialToToman } from '@/utilities/Financial';
import {
    getDefaultFulfillmentProfile,
    isShippingFulfillment,
    isZeroLaborFeeFulfillment,
    resolveFulfillmentServiceType,
    resolveOfferFulfillmentProfiles
} from '@/utilities/fulfillment';
import { GenerateFilePreviewUrl } from '@/utilities/GenerateFilePreviewUrl';
import { withSeparator } from '@/utilities/withSeparator';

import { SellerRowProps } from '../models';

export const SellerRow = ({ vendor, addToCartProps, isWinner, isSelected, onSelect }: SellerRowProps) => {
    const satisfaction = calculateSatisfaction(vendor.storeRating, vendor.storeReviewCount);
    const hasStoreRating = typeof vendor.storeRating === 'number' && !Number.isNaN(vendor.storeRating);
    const isAvailable = vendor.availableQuantity > 0;
    const hasEligibleFulfillment = Boolean(getDefaultFulfillmentProfile(vendor));
    const isDisabled = !isAvailable || !hasEligibleFulfillment;
    const finalPrice = vendor.sellingPrice || vendor.price;
    const hasDiscount = Boolean(vendor.discountPercentage && vendor.sellingPrice && vendor.sellingPrice < vendor.price);
    const isOfficial = vendor.storeName?.includes('رسمی');

    const deliveryTypes = useMemo(() => {
        return resolveOfferFulfillmentProfiles(vendor)
            .sort(
                (first, second) =>
                    resolveFulfillmentServiceType(first.fulfillmentType).priority - resolveFulfillmentServiceType(second.fulfillmentType).priority
            )
            .map((profile) => {
                const serviceType = resolveFulfillmentServiceType(profile.fulfillmentType);
                const fee = isShippingFulfillment(profile.fulfillmentType)
                    ? profile.isFree
                        ? 0
                        : profile.minBaseCost || 0
                    : isZeroLaborFeeFulfillment(profile.fulfillmentType)
                      ? 0
                      : profile.laborFee || 0;

                return {
                    id: profile.fulfillmentProfileId,
                    label: serviceType.label,
                    icon: serviceType.icon,
                    feeLabel: serviceType.feeLabel,
                    fee,
                    isFree: fee === 0,
                    fulfillmentType: profile.fulfillmentType,
                    isEligible: profile.isEligible === true
                };
            });
    }, [vendor]);

    const isSelectable = Boolean(onSelect && !isDisabled);

    const handleSelect = () => {
        if (!isSelectable) return;

        onSelect?.(vendor);
    };

    const rowClassName = [
        'relative grid grid-cols-1 items-center gap-3 rounded-lg border border-solid bg-white p-4 transition-all duration-200 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1.1fr)_minmax(0,0.8fr)_160px] sm:gap-x-6',
        isDisabled
            ? 'cursor-not-allowed border-gray-200 opacity-80'
            : isSelected
              ? 'border-primary bg-primary/[0.06] shadow-sm ring-4 ring-blue-100'
              : 'border-gray-200 hover:border-primary/60',
        isSelectable ? 'cursor-pointer' : isDisabled ? 'cursor-not-allowed' : 'cursor-default'
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <article
            role='listitem'
            tabIndex={isSelectable ? 0 : undefined}
            data-seller={vendor.storeUuid}
            data-disabled={isDisabled}
            aria-label={`فروشنده ${vendor.storeName}، قیمت نهایی ${withSeparator(rialToToman(finalPrice))} تومان`}
            onClick={isSelectable ? handleSelect : undefined}
            onKeyDown={(event) => {
                if (!isSelectable) return;

                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleSelect();
                }
            }}
            className={rowClassName}>
            {/* {isWinner && (
                <span className='absolute -top-3 right-4 z-10 rounded-lg bg-primary px-2.5 py-1 text-[10px] font-bold text-white'>
                    پیشنهاد ازکی‌سرویس
                </span>
            )} */}
            <div className='flex min-w-0 items-center gap-3'>
                <SafeImage
                    src={
                        GenerateFilePreviewUrl({
                            attachableId: vendor.storeUuid,
                            attachment: 'STORE',
                            type: 'LOGO',
                            isPrimary: true,
                            sortOrder: 0
                        }).src
                    }
                    alt={vendor.storeName}
                    fallback='/images/vendor/vendor_profile_default.png'
                    className='h-12 w-12 shrink-0 overflow-hidden rounded-lg'
                    width={48}
                    height={48}
                />
                <div className='flex min-w-0 flex-1 flex-col gap-1'>
                    <span className='flex flex-wrap items-center gap-1.5 text-sm font-semibold text-blackHighEmphasis sm:text-base'>
                        {vendor.storeName}
                        {vendor.storeCityName?.trim() && (
                            <span className='flex max-w-[45%] shrink-0 items-center border-0 border-s border-solid border-gray-200 ps-2'>
                                <StoreCityBadge cityName={vendor.storeCityName} compact />
                            </span>
                        )}
                        {isOfficial && <span className='rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary'>رسمی</span>}
                    </span>
                    <span className='flex flex-wrap items-center gap-1.5 text-[11px] text-blackMediumEmphasis sm:text-xs'>
                        {hasStoreRating ? (
                            <>
                                <span className={`inline-flex items-center gap-1 ${satisfaction.textClassName}`}>
                                    <RatingStarIcon size='sm' className='size-3.5' />
                                    {satisfaction.percent}٪ رضایت
                                </span>
                                <span aria-hidden>·</span>
                                <span>{withSeparator(vendor.storeReviewCount || 0)} نظر</span>
                            </>
                        ) : (
                            <span>بدون نظر</span>
                        )}
                    </span>
                </div>
            </div>

            <div
                className='flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-blackHighEmphasis sm:flex-col sm:items-start sm:gap-1.5
                    sm:text-xs'>
                {deliveryTypes.length ? (
                    deliveryTypes.map((item) => (
                        <span
                            key={item.id}
                            aria-disabled={!item.isEligible}
                            title={item.isEligible ? undefined : 'برای موقعیت شما قابل ارائه نیست'}
                            className={`inline-flex flex-nowrap items-center gap-1.5 whitespace-nowrap ${!item.isEligible ? 'opacity-80' : ''}`}>
                            <item.icon color='currentcolor' className='size-3.5 shrink-0 text-primary' />
                            <span className='font-semibold'>{item.label}</span>
                            {!['SHIPPING', 'PICKUP_CENTER'].includes(item.fulfillmentType) && (
                                <>
                                    <span aria-hidden>·</span>
                                    <span className='inline-flex items-center gap-1'>
                                        <span>{item.feeLabel}:</span>
                                        {item.isFree ? (
                                            <span className='!text-[10px] font-semibold text-green-700 sm:!text-[11px]'>رایگان</span>
                                        ) : (
                                            <span className='font-semibold text-primary'>
                                                {withSeparator(rialToToman(item.fee))} <span className='text-blackMediumEmphasis'>تومان</span>
                                            </span>
                                        )}
                                    </span>
                                </>
                            )}
                        </span>
                    ))
                ) : (
                    <span>بدون روش تحویل</span>
                )}
            </div>

            <div className='flex flex-col gap-0.5 sm:items-end sm:pe-2'>
                {isAvailable ? (
                    addToCartProps.isPriceLoading ? (
                        <span className='h-6 w-28 animate-pulse rounded bg-gray-100' />
                    ) : (
                        <>
                            {hasDiscount && (
                                <span className='flex flex-wrap items-center gap-2'>
                                    <span className='rounded-lg bg-[#fbbf24] px-2 text-[10px] font-semibold text-blackHighEmphasis'>
                                        {vendor.discountPercentage}٪
                                    </span>
                                    <span className='text-[11px] text-blackLowEmphasis line-through sm:text-xs'>
                                        {withSeparator(rialToToman(vendor.price))}
                                    </span>
                                </span>
                            )}
                            <span className='text-sm font-bold text-primary sm:text-base'>
                                {withSeparator(rialToToman(finalPrice))}{' '}
                                <small className='text-[11px] font-normal text-blackHighEmphasis sm:text-xs'>تومان</small>
                            </span>
                        </>
                    )
                ) : (
                    <span className='text-xs font-semibold text-red-600 sm:text-sm'>ناموجود</span>
                )}
            </div>

            {onSelect ? (
                <Button
                    variant={isSelected && !isDisabled ? 'contained' : 'outlined'}
                    color='primary'
                    disabled={isDisabled}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleSelect();
                    }}
                    className='!h-11 min-w-[138px] whitespace-nowrap px-4 sm:ms-2 sm:!h-10'>
                    {!isAvailable ? 'ناموجود' : !hasEligibleFulfillment ? 'خارج از محدوده' : isSelected ? 'انتخاب شده' : 'انتخاب فروشنده'}
                </Button>
            ) : (
                <div className='w-36 sm:ms-2'>
                    <AddToCartButton
                        available={!isDisabled}
                        isPriceLoading={addToCartProps.isPriceLoading || false}
                        href={addToCartProps.href}
                        isMainVendor={Boolean(addToCartProps.isMainVendor)}
                        variant={isWinner ? 'contained' : 'outlined'}
                        allStores={[vendor]}
                        winner={vendor}
                    />
                </div>
            )}
        </article>
    );
};
